import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Paper, Rating, Chip, Alert, CircularProgress, Breadcrumbs, Link, IconButton, Tooltip, Button, useMediaQuery, useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useParams, useNavigate } from 'react-router-dom';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import apiClient from '../config/axios';
import dayjs from 'dayjs';
import ModalProposedGrade from '../components/ModalProposedGrade';
import ModalConfirm from '../components/ModalConfirm';
import ModalResult from '../components/ModalResult';
import InfoWeightedAverage from '../components/InfoWeightedAverage';
import WallDetailGraph from '../components/WallDetailGraph';
import { useAuth } from '../contexts/AuthContext';
import getConfig from '../config/app-config';

const WallDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [wall, setWall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteData, setDeleteData] = useState(null);
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [resultData, setResultData] = useState({ message: '', severity: 'info' });
  const [addGradeModalOpen, setAddGradeModalOpen] = useState(false);
  const getWallColorHex = (color) => {
    const wallColorMap = {
      giallo: '#FBC02D',
      gialla: '#FBC02D',
      nero: '#212121',
      nera: '#212121',
      verde: '#388E3C',
      rossa: '#D32F2F',
      blu: '#1976D2',
      bianco: '#FAFAFA',
      bianca: '#FAFAFA',
      arancione: '#F57C00',
      viola: '#8E24AA',
    };
    return wallColorMap[color] || '#9E9E9E';
  };

  const fetchWall = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/walls/${id}`);
      setWall(response.data);
    } catch (err) {
      console.error('Errore nel caricamento della via:', err);
      setError('Errore nel caricamento della via. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const config = getConfig();
  // Verifica che sia Rock Dreams
  if (!config.WITH_WALLS) {
    return (
      <Container maxWidth='lg' sx={{ mt: 4, mb: 4 }}>
        <Alert severity='error'>Questa funzionalità è disponibile solo per palestre di arrampicata.</Alert>
      </Container>
    );
  }

  const handleEdit = (proposedGrade) => {
    setEditData(proposedGrade);
    setEditModalOpen(true);
  };

  const handleDelete = (proposedGrade) => {
    setDeleteData(proposedGrade);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await apiClient.delete(`/api/walls/${id}/proposed-grades`);

      setResultData({
        message: 'Grado proposto eliminato con successo!',
        severity: 'success',
      });
      setResultModalOpen(true);

      // Ricarica i dati
      await fetchWall();
    } catch (err) {
      console.error("Errore nell'eliminazione del grado proposto:", err);

      let errorMessage = "Errore nell'eliminazione del grado proposto";
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }

      setResultData({
        message: errorMessage,
        severity: 'error',
      });
      setResultModalOpen(true);
    } finally {
      setDeleteConfirmOpen(false);
      setDeleteData(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setDeleteData(null);
  };

  const handleEditSuccess = async () => {
    setEditModalOpen(false);
    setEditData(null);
    await fetchWall();
  };

  const handleResultClose = () => {
    setResultModalOpen(false);
  };

  const handleAddGrade = () => {
    setAddGradeModalOpen(true);
  };

  const handleAddGradeClose = () => {
    setAddGradeModalOpen(false);
  };

  const handleAddGradeSuccess = () => {
    fetchWall(); // Ricarica i dati per aggiornare la tabella
  };

  const isOwner = (proposedGrade) => {
    return user && proposedGrade.userId && user._id === proposedGrade.userId._id;
  };

  const hasUserProposedGrade = (proposedGrade) => {
    return isOwner(proposedGrade);
  };

  const hasUserProposedGradeForWall = () => {
    if (!user || !wall?.proposedGrades) return false;
    return wall.proposedGrades.some((pg) => pg.userId && pg.userId._id === user._id);
  };

  const getUserProposedGrade = () => {
    if (!user || !wall?.proposedGrades) return null;
    return wall.proposedGrades.find((pg) => pg.userId && pg.userId._id === user._id);
  };

  // console.log('isMobile', isMobile);

  const columns = [
    {
      field: 'createdAt',
      headerName: 'Data',
      // width: 120,
      flex: 0.5,
      renderCell: (params) => {
        return dayjs(params.value).format('DD MMM YY');
      },
    },
    {
      field: 'user',
      headerName: 'Utente',
      flex: 0.8,
      // minWidth: 120,
      renderCell: (params) => {
        const user = params.row.userId;
        if (!user) return 'N/A';
        return (
          <Typography variant='body2' fontWeight='medium'>
            {user.name} {user.surname}
            {isMobile && params.row.notes && (
              <Typography
                variant='caption'
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxWidth: '100%',
                  mt: 0,
                }}
              >
                <br />
                {params.row.notes}
              </Typography>
            )}
          </Typography>
        );
      },
      valueGetter: (value, row) => {
        const user = row.userId;
        return user ? `${user.name} ${user.surname}`.toLowerCase() : '';
      },
    },
    {
      field: 'grade',
      headerName: 'Grado',
      // width: 160,
      flex: 0.7,
      renderCell: (params) => {
        // console.log('params', params);
        return (
          <Box display='flex' alignItems='center' flexDirection='column' gap={1}>
            <Chip label={params.value || '-'} color='secondary' variant='contained' size='small' />
            {isMobile && <Rating value={params.row.rating || 0} precision={0.1} readOnly size='small' />}
          </Box>
        );
      },
    },
    {
      field: 'rating',
      headerName: 'Bellezza',
      // width: 150,
      flex: 1.2,
      renderCell: (params) => (
        <Box>
          <Rating value={params.value || 0} precision={0.1} readOnly size='small' />
        </Box>
      ),
    },
    {
      field: 'notes',
      headerName: 'Note',
      flex: 1,
      // minWidth: 200,
      renderCell: (params) => (
        <Typography
          variant='body2'
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxWidth: '100%',
          }}
        >
          {params.value || '-'}
        </Typography>
      ),
    },
    // {
    //   field: 'actions',
    //   headerName: '',
    //   // width: 120,
    //   flex: 0.5,
    //   sortable: false,
    //   filterable: false,
    //   renderCell: (params) => {
    //     const proposedGrade = params.row;
    //     const canEdit = isOwner(proposedGrade);

    //     if (!canEdit) return null;

    //     return (
    //       <Box display='flex' gap={1}>
    //         <Tooltip title='Modifica grado proposto'>
    //           <IconButton onClick={() => handleEdit(proposedGrade)} color='primary' size='small'>
    //             <EditIcon />
    //           </IconButton>
    //         </Tooltip>
    //         <Tooltip title='Elimina grado proposto'>
    //           <IconButton onClick={() => handleDelete(proposedGrade)} color='error' size='small'>
    //             <DeleteIcon />
    //           </IconButton>
    //         </Tooltip>
    //       </Box>
    //     );
    //   },
    // },
  ];

  if (loading) {
    return (
      <Container maxWidth='lg' sx={{ mt: 4, mb: 4 }}>
        <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth='lg' sx={{ mt: 4, mb: 4 }}>
        <Alert severity='error'>{error}</Alert>
      </Container>
    );
  }

  if (!wall) {
    return (
      <Container maxWidth='lg' sx={{ mt: 4, mb: 4 }}>
        <Alert severity='error'>Via non trovata</Alert>
      </Container>
    );
  }

  const proposedGrades = wall.proposedGrades || [];

  return (
    // <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
    <Box sx={{ mt: 4, mb: 4, maxWidth: 'lg', marginLeft: 'auto', marginRight: 'auto' }}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component='button'
          variant='body1'
          onClick={() => navigate('/walls')}
          // sx={{ textDecoration: 'none' }}
        >
          Vie di Arrampicata
        </Link>
        {wall.wallNumber && wall.wallColor ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <Typography color='text.primary'>{`Parete ${wall.wallNumber}`}</Typography>
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                bgcolor: getWallColorHex(wall.wallColor),
                border: wall.wallColor === 'bianco' ? '1px solid #BDBDBD' : 'none',
              }}
            />
          </Box>
        ) : (
          <Typography color='text.primary'>{wall.name || 'Via'}</Typography>
        )}
      </Breadcrumbs>

      <WallDetailGraph proposedGrades={proposedGrades} />

      <Box sx={{ mb: 3 }}>
        {wall.wallNumber && wall.wallColor ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1 }}>
            <Typography variant='h4' component='h1' sx={{ mb: 0 }}>
              {`Parete ${wall.wallNumber}`}
            </Typography>
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                bgcolor: getWallColorHex(wall.wallColor),
                border: wall.wallColor === 'bianco' ? '1px solid #BDBDBD' : 'none',
              }}
            />
          </Box>
        ) : (
          <Typography variant='h4' component='h1' gutterBottom>
            {wall.name || 'Via'}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
          {wall.officialGrade && <Chip label={`Grado Ufficiale: ${wall.officialGrade}`} color='primary' variant='contained' />}
          {wall.averageGrade && <Chip label={`Grado Proposto: ${wall.averageGrade}`} color='secondary' variant='contained' />}
          {/* <Chip label={`${proposedGrades.length} Ripetizioni`} color='info' variant='contained' /> */}
          <InfoWeightedAverage />
        </Box>
        {wall.notes && (
          <Typography variant='body1' color='text.secondary' sx={{ mb: 2 }}>
            {wall.notes}
          </Typography>
        )}
      </Box>

      <Paper sx={{ backgroundColor:'primary.main', width: !isMobile ? '100%' : 'calc(100% + 48px)', marginLeft: { xs: -2, sm: -6, md: 0 }, marginRight: { xs: -2, sm: -6, md: 0 } }}>
        {/* <Paper> */}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderTopLeftRadius: 15, borderTopRightRadius: 15, borderBottom: '1px solid #e0e0e0', backgroundColor: 'primary.main', color: 'white' }}>
          <Typography variant='h6' component='h2'>
            Ripetizioni
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {user && hasUserProposedGradeForWall() && (
              <>
                <Tooltip title='Modifica grado proposto'>
                  <IconButton onClick={() => handleEdit(getUserProposedGrade())} color='warning' size='small'>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Elimina grado proposto'>
                  <IconButton onClick={() => handleDelete(getUserProposedGrade())} color='warning' size='small'>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {user && !hasUserProposedGradeForWall() && (
              <Tooltip title='Aggiungi ripetizione'>
                
                <Button variant='contained' startIcon={<AddIcon />} onClick={handleAddGrade} color='secondary' size='small' sx={{ '&:hover': { backgroundColor: 'secondary.main', color: 'white' } }}>
                  Aggiungi Ripetizione
                </Button>
              </Tooltip>
            )}
          </Box>
        </Box>
        <DataGrid
          disableColumnMenu
          rows={proposedGrades}
          columns={columns}
          getRowId={(row, index) => `${row.userId?._id || 'unknown'}-${index}`}
          // rowHeight={80}
          getRowHeight={() => (isMobile ? 'auto' : 80)}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25, page: 0 },
            },
            sorting: {
              sortModel: [{ field: 'updatedAt', sort: 'desc' }],
            },

            columns: {
              columnVisibilityModel: {
                // Hide columns status and traderName, the other columns will remain visible
                notes: isMobile ? false : true,
                rating: isMobile ? false : true,
              },
            },
          }}
          rowsPerPageOptions={[25, 50, 100]}
          disableSelectionOnClick
          hideFooterPagination={proposedGrades.length <= 25}
          sx={{
            // applica un border rosso a tutto il datagrid
            border: '0px solid red',
            borderRadius: 0,
            // backgroundColor: '#ee44aa',
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              alignItems: 'center',
              verticalAlign: 'middle',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#ffffff',
            },
            '& .MuiDataGrid-columnHeader': {
              textAlign: 'left',
              justifyContent: 'flex-start',
            },
            '& .MuiDataGrid-row.user-proposed-grade': {
              backgroundColor: '#fff9c4',
              '&:hover': {
                backgroundColor: '#fff9c4 !important',
              },
            },
            // '& .MuiDataGrid-cellContent': {
            //   display: 'flex',
            //   alignItems: 'center',
            //   height: '100%',
            // },
          }}
          getRowClassName={(params) => {
            return hasUserProposedGrade(params.row) ? 'user-proposed-grade' : '';
          }}
        />
      </Paper>

      {/* Modal per edit */}
      <ModalProposedGrade
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditData(null);
        }}
        wallId={id}
        onSuccess={handleEditSuccess}
        editData={editData}
        isEdit={true}
      />

      {/* Modal per conferma eliminazione */}
      <ModalConfirm open={deleteConfirmOpen} title='Conferma Eliminazione' message={`Sei sicuro di voler eliminare il tuo grado proposto "${deleteData?.grade}"?`} onConfirm={handleConfirmDelete} onCancel={handleCancelDelete} />

      {/* Modal per risultato */}
      <ModalResult open={resultModalOpen} onClose={handleResultClose} message={resultData.message} severity={resultData.severity} title={resultData.severity === 'success' ? 'Successo' : 'Errore'} />

      {/* Modal per aggiungere grado proposto */}
      <ModalProposedGrade open={addGradeModalOpen} onClose={handleAddGradeClose} wallId={id} onSuccess={handleAddGradeSuccess} />
    </Box>
  );
};

export default WallDetail;
